import React, { useState, useEffect, useRef } from 'react';
import { Camera, RefreshCw, ArrowRight, Check, Edit3, ChevronDown } from 'lucide-react';
import authService from '../services/authService';
import { useToast } from '../hooks/useToast';
import authStorage from '../utils/authStorage';
import { fetchStates, fetchCitiesForState } from '../services/locationService';

const UserProfile = () => {
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  // User details state
  const [userData, setUserData] = useState({
    id: '',
    name: '',
    emailId: '',
    mobileNo: '',
    photoUrl: '',
    signupAs: '',
    state: '',
    city: ''
  });

  // Profile form state (Name & Location)
  const [nameInput, setNameInput] = useState('');
  const [stateInput, setStateInput] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileErrors, setProfileErrors] = useState({ name: '', state: '', city: '' });

  // Email update OTP state
  const [emailStep, setEmailStep] = useState('IDLE'); // 'IDLE' | 'INPUT' | 'OTP'
  const [newEmail, setNewEmail] = useState('');
  const [emailOtp, setEmailOtp] = useState(['', '', '', '', '', '']);
  const [sendingEmailOtp, setSendingEmailOtp] = useState(false);
  const [verifyingEmailOtp, setVerifyingEmailOtp] = useState(false);
  const [emailTimer, setEmailTimer] = useState(0);
  const [emailError, setEmailError] = useState('');

  // Mobile update OTP state
  const [mobileStep, setMobileStep] = useState('IDLE'); // 'IDLE' | 'INPUT' | 'OTP'
  const [newMobile, setNewMobile] = useState('');
  const [mobileOtp, setMobileOtp] = useState(['', '', '', '', '', '']);
  const [sendingMobileOtp, setSendingMobileOtp] = useState(false);
  const [verifyingMobileOtp, setVerifyingMobileOtp] = useState(false);
  const [mobileTimer, setMobileTimer] = useState(0);
  const [mobileError, setMobileError] = useState('');

  // Avatar uploading
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    const loadStates = async () => {
      const list = await fetchStates();
      setStatesList(list);
    };
    loadStates();
  }, []);

  const handleStateSelectInProfile = async (selState) => {
    setStateInput(selState);
    setCityInput('');
    setProfileErrors((prev) => ({ ...prev, state: '', city: '' }));
    if (selState) {
      setLoadingCities(true);
      const cities = await fetchCitiesForState(selState);
      setCitiesList(cities);
      setLoadingCities(false);
    } else {
      setCitiesList([]);
    }
  };

  // Unified Profile Saving (Name & Location) with Client-Side Validation
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    const errors = { name: '', state: '', city: '' };
    let hasError = false;

    const nameVal = nameInput.trim();
    if (!nameVal) {
      errors.name = 'Full Name is required';
      hasError = true;
    } else if (nameVal.length < 2) {
      errors.name = 'Full Name must be at least 2 characters';
      hasError = true;
    } else if (!/^[a-zA-Z\s.'-]+$/.test(nameVal)) {
      errors.name = 'Full Name can only contain letters, spaces, dots, hyphens, and apostrophes';
      hasError = true;
    }

    if (!stateInput) {
      errors.state = 'Please select a state';
      hasError = true;
    }
    if (!cityInput) {
      errors.city = 'Please select a city';
      hasError = true;
    }

    if (hasError) {
      setProfileErrors(errors);
      return;
    }
    setProfileErrors({ name: '', state: '', city: '' });

    setSavingProfile(true);
    try {
      let nameUpdated = false;
      let locationUpdated = false;

      // 1. Save Name if modified
      if (nameInput.trim() !== userData.name) {
        const resName = await authService.updateProfileName(nameInput.trim());
        if (resName.success) {
          nameUpdated = true;
          window.dispatchEvent(new Event('profilePhotoChanged'));
        } else {
          toast.error(resName.error || 'Failed to update name');
          setSavingProfile(false);
          return;
        }
      }

      // 2. Save Location if modified
      if (stateInput !== userData.state || cityInput !== userData.city) {
        const resLoc = await authService.updateProfileLocation(stateInput, cityInput);
        if (resLoc.success) {
          locationUpdated = true;
        } else {
          toast.error(resLoc.error || 'Failed to update location');
          setSavingProfile(false);
          return;
        }
      }

      if (nameUpdated || locationUpdated) {
        toast.success('Profile details updated successfully!');
        loadProfile();
      } else {
        toast.info('No changes were made to profile details.');
      }
    } catch (err) {
      console.error(err);
      toast.error('An error occurred while saving profile details');
    } finally {
      setSavingProfile(false);
    }
  };

  // Load user profile on mount
  const loadProfile = async () => {
    const storedUser = JSON.parse(authStorage.getItem('user') || '{}');
    const email = storedUser.emailId || storedUser.username;

    if (email && email.includes('@')) {
      const res = await authService.getUserProfile(email);
      if (res.success && res.data) {
        const u = res.data;
        const signupAsVal = u.signupAs || storedUser.signupAs || 'Vendor/Supplier';
        setUserData({
          id: u.id || '',
          name: u.name || '',
          emailId: u.emailId || '',
          mobileNo: u.mobileNo || '',
          photoUrl: u.photoUrl || '',
          signupAs: signupAsVal,
          state: u.state || '',
          city: u.city || ''
        });
        setNameInput(u.name || '');
        setStateInput(u.state || '');
        if (u.state) {
          fetchCitiesForState(u.state).then(c => {
            setCitiesList(c);
            setCityInput(u.city || '');
          });
        }
        authStorage.setItem('user', JSON.stringify({
          ...storedUser,
          id: u.id,
          name: u.name,
          emailId: u.emailId,
          mobileNo: u.mobileNo || '',
          photoUrl: u.photoUrl || '',
          signupAs: signupAsVal,
          state: u.state || '',
          city: u.city || ''
        }));
        return;
      }
    }

    setUserData({
      id: storedUser.id || '',
      name: storedUser.name || '',
      emailId: storedUser.emailId || storedUser.username || '',
      mobileNo: storedUser.mobileNo || '',
      photoUrl: storedUser.photoUrl || '',
      signupAs: storedUser.signupAs || 'Vendor/Supplier',
      state: storedUser.state || '',
      city: storedUser.city || ''
    });
    setNameInput(storedUser.name || '');
    setStateInput(storedUser.state || '');
  };

  useEffect(() => {
    loadProfile();
  }, []);

  // Email Timer countdown
  useEffect(() => {
    let interval = null;
    if (emailTimer > 0) {
      interval = setInterval(() => {
        setEmailTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [emailTimer]);

  // Mobile Timer countdown
  useEffect(() => {
    let interval = null;
    if (mobileTimer > 0) {
      interval = setInterval(() => {
        setMobileTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [mobileTimer]);

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Helper for image URLs
  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
    return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
  };

  // ── Profile Photo Upload ───────────────────────────────────────────────────
  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('photo', file);
    setUploadingPhoto(true);

    try {
      const res = await authService.uploadProfilePhoto(formData);
      if (res.success && res.data) {
        toast.success('Profile picture updated successfully!');
        const newPhotoUrl = res.data.photoUrl || res.data.user?.photoUrl;
        setUserData((prev) => ({ ...prev, photoUrl: newPhotoUrl }));

        const storedUser = authStorage.getItem('user');
        if (storedUser) {
          const userObj = JSON.parse(storedUser);
          userObj.photoUrl = newPhotoUrl;
          authStorage.setItem('user', JSON.stringify(userObj));
        }

        window.dispatchEvent(new Event('profilePhotoChanged'));
      } else {
        toast.error(res.error || 'Failed to upload image');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error uploading profile picture');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // ── Email Update OTP ───────────────────────────────────────────────────────
  const handleRequestEmailOtp = async (e) => {
    e.preventDefault();
    setEmailError('');
    const targetEmail = newEmail.trim().toLowerCase();
    if (!targetEmail) {
      setEmailError('Email address is required');
      return;
    }
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(targetEmail)) {
      setEmailError('Please enter a valid email address (e.g. name@example.com)');
      return;
    }
    if (targetEmail === userData.emailId.toLowerCase()) {
      setEmailError('New email is identical to your current email');
      return;
    }

    setSendingEmailOtp(true);
    try {
      const res = await authService.requestEmailOtp(targetEmail);
      if (res.success) {
        toast.success(`OTP code sent to ${targetEmail}`);
        setEmailStep('OTP');
        setEmailTimer(180);
        setEmailOtp(['', '', '', '', '', '']);
        setEmailError('');
      } else {
        const err = res.error || 'Failed to send OTP to email';
        setEmailError(err);
      }
    } catch (err) {
      console.error(err);
      setEmailError('An error occurred while requesting email OTP');
    } finally {
      setSendingEmailOtp(false);
    }
  };

  const handleVerifyEmailOtp = async (e) => {
    e.preventDefault();
    setEmailError('');
    const code = emailOtp.join('');
    if (code.length < 6) {
      setEmailError('Please enter complete 6-digit OTP');
      return;
    }

    setVerifyingEmailOtp(true);
    try {
      const res = await authService.verifyEmailOtp(newEmail.trim().toLowerCase(), code);
      if (res.success && res.data) {
        toast.success('Email address updated successfully!');
        const updatedEmail = res.data.user?.emailId || newEmail.trim().toLowerCase();
        setUserData((prev) => ({ ...prev, emailId: updatedEmail }));
        setEmailStep('IDLE');
        setNewEmail('');
        setEmailOtp(['', '', '', '', '', '']);
        setEmailError('');
        window.dispatchEvent(new Event('profilePhotoChanged'));
      } else {
        const err = res.error || 'Invalid OTP verification code';
        setEmailError(err);
      }
    } catch (err) {
      console.error(err);
      setEmailError('An error occurred while verifying email OTP');
    } finally {
      setVerifyingEmailOtp(false);
    }
  };

  const handleEmailOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...emailOtp];
    newOtp[index] = value.slice(-1);
    setEmailOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.getElementById(`email-otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  // ── Mobile Update OTP ──────────────────────────────────────────────────────
  const handleRequestMobileOtp = async (e) => {
    e.preventDefault();
    setMobileError('');
    const targetMobile = newMobile.trim();
    if (!targetMobile) {
      setMobileError('Mobile number is required');
      return;
    }
    if (!/^[6-9]\d{9}$/.test(targetMobile)) {
      setMobileError('Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9');
      return;
    }
    if (targetMobile === userData.mobileNo.trim()) {
      setMobileError('New mobile number is identical to your current mobile number');
      return;
    }

    setSendingMobileOtp(true);
    try {
      const res = await authService.requestMobileOtp(targetMobile);
      if (res.success) {
        toast.success(`OTP code sent via SMS to ${targetMobile}`);
        setMobileStep('OTP');
        setMobileTimer(180);
        setMobileOtp(['', '', '', '', '', '']);
        setMobileError('');
      } else {
        const err = res.error || 'Failed to send SMS OTP';
        setMobileError(err);
      }
    } catch (err) {
      console.error(err);
      setMobileError('An error occurred while requesting mobile OTP');
    } finally {
      setSendingMobileOtp(false);
    }
  };

  const handleVerifyMobileOtp = async (e) => {
    e.preventDefault();
    setMobileError('');
    const code = mobileOtp.join('');
    if (code.length < 6) {
      setMobileError('Please enter complete 6-digit OTP');
      return;
    }

    setVerifyingMobileOtp(true);
    try {
      const res = await authService.verifyMobileOtp(newMobile.trim(), code);
      if (res.success && res.data) {
        toast.success('Mobile number updated successfully!');
        const updatedMobile = res.data.user?.mobileNo || newMobile.trim();
        setUserData((prev) => ({ ...prev, mobileNo: updatedMobile }));
        setMobileStep('IDLE');
        setNewMobile('');
        setMobileOtp(['', '', '', '', '', '']);
        setMobileError('');
        window.dispatchEvent(new Event('profilePhotoChanged'));
      } else {
        const err = res.error || 'Invalid OTP verification code';
        setMobileError(err);
      }
    } catch (err) {
      console.error(err);
      setMobileError('An error occurred while verifying mobile OTP');
    } finally {
      setVerifyingMobileOtp(false);
    }
  };

  const handleMobileOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...mobileOtp];
    newOtp[index] = value.slice(-1);
    setMobileOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.getElementById(`mobile-otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Unified Profile Card Container */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs p-4 sm:p-6 md:p-8 space-y-6">
        
        {/* Page Header & Avatar */}
        <div className="flex flex-col items-center justify-center text-center pb-6 border-b border-gray-100">
          <div className="relative mb-3">
            <div className="w-20 h-20 sm:w-22 sm:h-22 rounded-full bg-[#e0f7f8] border-2 border-[#2ab391]/20 flex items-center justify-center overflow-hidden shadow-xs">
              {userData.photoUrl ? (
                <img
                  src={getImageUrl(userData.photoUrl)}
                  alt="User Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#2ab391] to-[#1b8a70] text-white font-bold text-2xl sm:text-3xl flex items-center justify-center select-none">
                  {(userData.name || 'U').charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-[#2ab391] hover:bg-[#239b7d] text-white flex items-center justify-center shadow-md transition-all cursor-pointer"
              title="Change Photo"
            >
              <Camera size={13} />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handlePhotoSelect}
              accept="image/*"
              className="hidden"
            />
          </div>

          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
            {userData.name || 'User Profile'}
          </h1>
          <p className="text-xs text-gray-500 font-medium mt-0.5">
            Manage your account details and contact preferences
          </p>
        </div>

        {/* ── Main Profile Form (Name, State, City + Single Save Button at Bottom) ── */}
        <form onSubmit={handleSaveProfile} noValidate className="space-y-4">
          {/* Full Name */}
          <div className="space-y-1">
            <label className="block text-xs font-semibold text-gray-700">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={nameInput}
              onChange={(e) => {
                setNameInput(e.target.value);
                setProfileErrors((prev) => ({ ...prev, name: '' }));
              }}
              placeholder="Enter your full name"
              className={`w-full border rounded-xl px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 bg-white transition-all shadow-2xs text-gray-800 h-10 ${
                profileErrors.name ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-[#2ab391]'
              }`}
            />
            {profileErrors.name && (
              <p className="text-xs text-red-500 font-medium mt-1">{profileErrors.name}</p>
            )}
          </div>

          {/* Location Grid (State & City) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* State Select Dropdown */}
            <div className="space-y-1">
              <label className="block text-xs font-semibold text-gray-700">
                State <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={stateInput}
                  onChange={(e) => handleStateSelectInProfile(e.target.value)}
                  className={`w-full border rounded-xl pl-3.5 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 bg-white transition-all shadow-2xs text-gray-800 h-11 sm:h-10 cursor-pointer appearance-none font-medium ${
                    profileErrors.state ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-[#2ab391]'
                  }`}
                >
                  <option value="">Select State</option>
                  {statesList.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" />
              </div>
              {profileErrors.state && (
                <p className="text-xs text-red-500 font-medium mt-1">{profileErrors.state}</p>
              )}
            </div>

            {/* City Select Dropdown */}
            <div className="space-y-1">
              <label className="block text-xs font-semibold text-gray-700">
                City <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={cityInput}
                  onChange={(e) => {
                    setCityInput(e.target.value);
                    setProfileErrors((prev) => ({ ...prev, city: '' }));
                  }}
                  disabled={!stateInput || loadingCities}
                  className={`w-full border rounded-xl pl-3.5 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 bg-white transition-all shadow-2xs text-gray-800 h-11 sm:h-10 cursor-pointer appearance-none font-medium ${
                    !stateInput ? 'bg-gray-50 text-gray-400 border-gray-300' : profileErrors.city ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-[#2ab391]'
                  }`}
                >
                  <option value="">{loadingCities ? 'Loading cities...' : !stateInput ? 'Select State First' : 'Select City'}</option>
                  {citiesList.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" />
              </div>
              {profileErrors.city && (
                <p className="text-xs text-red-500 font-medium mt-1">{profileErrors.city}</p>
              )}
            </div>
          </div>

          {/* Single Save Profile Button at the Bottom of Name & Location */}
          <div className="pt-2 flex justify-start sm:justify-end">
            <button
              type="submit"
              disabled={savingProfile}
              className="bg-[#2ab391] hover:bg-[#239b7d] text-white font-semibold px-5 h-10 rounded-xl text-xs transition-all shadow-2xs flex items-center justify-center cursor-pointer disabled:opacity-50 w-auto"
            >
              <span>{savingProfile ? 'Updating Profile...' : 'Update Profile'}</span>
            </button>
          </div>
        </form>

        {/* ── Divider ── */}
        <div className="border-t border-gray-100 pt-5 space-y-5">
          
          {/* 3. Email Address Update Section */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-gray-700">
              Email Address
            </label>
            
            {emailStep === 'IDLE' && (
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2 text-sm text-gray-800 font-medium h-10 flex items-center min-w-0">
                  <span className="truncate">{userData.emailId || 'Not provided'}</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setEmailStep('INPUT');
                    setNewEmail('');
                    setEmailError('');
                  }}
                  className="border border-[#2ab391] text-[#2ab391] hover:bg-[#e0f7f8] font-semibold px-3 sm:px-4 h-10 rounded-xl text-xs transition-all shadow-2xs flex items-center justify-center gap-1.5 cursor-pointer flex-shrink-0"
                  title="Update Email"
                >
                  <Edit3 size={15} />
                  <span className="hidden sm:inline">Update</span>
                </button>
              </div>
            )}

            {emailStep === 'INPUT' && (
              <form onSubmit={handleRequestEmailOtp} noValidate className="space-y-3 bg-slate-50/80 p-4 rounded-xl border border-gray-200/80">
                <div>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => {
                      setNewEmail(e.target.value);
                      setEmailError('');
                    }}
                    placeholder="name@example.com"
                    className={`w-full border rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 bg-white transition-all shadow-2xs text-gray-800 ${
                      emailError ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-[#2ab391]'
                    }`}
                  />
                  {emailError && (
                    <p className="text-xs text-red-500 font-medium mt-1">{emailError}</p>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEmailStep('IDLE');
                      setEmailError('');
                    }}
                    className="px-3.5 py-2 rounded-xl text-xs font-medium text-gray-600 hover:bg-gray-200/60 transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sendingEmailOtp}
                    className="bg-[#2ab391] hover:bg-[#239b7d] text-white font-semibold px-4 py-2 rounded-xl text-xs transition-all shadow-2xs flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                  >
                    <span>{sendingEmailOtp ? 'Sending OTP...' : 'Send OTP'}</span>
                    <ArrowRight size={13} />
                  </button>
                </div>
              </form>
            )}

            {emailStep === 'OTP' && (
              <form onSubmit={handleVerifyEmailOtp} noValidate className="space-y-3 bg-slate-50/80 p-4 rounded-xl border border-gray-200/80">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-gray-700 truncate pr-2">
                    OTP sent to <span className="font-bold text-gray-900">{newEmail}</span>
                  </span>
                  {emailTimer > 0 ? (
                    <span className="text-[#2ab391] font-semibold flex-shrink-0">{formatTimer(emailTimer)}</span>
                  ) : (
                    <span className="text-red-500 font-semibold flex-shrink-0">Expired</span>
                  )}
                </div>

                {/* 6-Digit OTP Box Grid */}
                <div className="flex items-center justify-center gap-1 sm:gap-2 py-1">
                  {emailOtp.map((digit, idx) => (
                    <input
                      key={idx}
                      id={`email-otp-${idx}`}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => {
                        handleEmailOtpChange(idx, e.target.value);
                        setEmailError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !digit && idx > 0) {
                          const prevInput = document.getElementById(`email-otp-${idx - 1}`);
                          if (prevInput) prevInput.focus();
                        }
                      }}
                      className={`w-9 sm:w-10 h-10 sm:h-11 border rounded-xl text-center font-bold text-base focus:outline-none focus:ring-2 focus:ring-[#2ab391] bg-white shadow-2xs transition-all flex-shrink-0 ${
                        emailError ? 'border-red-500' : 'border-gray-300 focus:border-[#2ab391]'
                      }`}
                    />
                  ))}
                </div>

                {emailError && (
                  <p className="text-xs text-red-500 font-medium text-center mt-1">{emailError}</p>
                )}

                <div className="flex items-center justify-between gap-2 pt-0.5">
                  <button
                    type="button"
                    onClick={handleRequestEmailOtp}
                    disabled={sendingEmailOtp || emailTimer > 150}
                    className="text-[11px] font-semibold text-[#2ab391] hover:underline disabled:opacity-40 flex items-center gap-1 cursor-pointer"
                  >
                    <RefreshCw size={11} /> Resend OTP
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEmailStep('INPUT');
                        setEmailError('');
                      }}
                      className="text-gray-500 hover:bg-gray-200/60 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={verifyingEmailOtp || emailOtp.join('').length < 6}
                      className="bg-[#2ab391] hover:bg-[#239b7d] text-white font-semibold px-3.5 py-1.5 rounded-xl text-xs transition-all shadow-xs cursor-pointer disabled:opacity-50 flex items-center gap-1"
                    >
                      <Check size={14} />
                      <span>{verifyingEmailOtp ? 'Verifying...' : 'Verify'}</span>
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* 4. Mobile Number Update Section */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-gray-700">
              Mobile Number
            </label>

            {mobileStep === 'IDLE' && (
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2 text-sm text-gray-800 font-medium h-10 flex items-center min-w-0">
                  <span className="truncate">{userData.mobileNo ? `+91 ${userData.mobileNo}` : 'Not registered'}</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setMobileStep('INPUT');
                    setNewMobile('');
                    setMobileError('');
                  }}
                  className="border border-[#2ab391] text-[#2ab391] hover:bg-[#e0f7f8] font-semibold px-3 sm:px-4 h-10 rounded-xl text-xs transition-all shadow-2xs flex items-center justify-center gap-1.5 cursor-pointer flex-shrink-0"
                  title="Update Mobile"
                >
                  <Edit3 size={15} />
                  <span className="hidden sm:inline">Update</span>
                </button>
              </div>
            )}

            {mobileStep === 'INPUT' && (
              <form onSubmit={handleRequestMobileOtp} noValidate className="space-y-3 bg-slate-50/80 p-4 rounded-xl border border-gray-200/80">
                <div>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-semibold">
                      +91
                    </span>
                    <input
                      type="tel"
                      maxLength={10}
                      value={newMobile}
                      onChange={(e) => {
                        setNewMobile(e.target.value.replace(/\D/g, ''));
                        setMobileError('');
                      }}
                      placeholder="9876543210"
                      className={`w-full border rounded-xl pl-11 pr-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 bg-white transition-all shadow-2xs text-gray-800 ${
                        mobileError ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-[#2ab391]'
                      }`}
                    />
                  </div>
                  {mobileError && (
                    <p className="text-xs text-red-500 font-medium mt-1">{mobileError}</p>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setMobileStep('IDLE');
                      setMobileError('');
                    }}
                    className="px-3.5 py-2 rounded-xl text-xs font-medium text-gray-600 hover:bg-gray-200/60 transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sendingMobileOtp}
                    className="bg-[#2ab391] hover:bg-[#239b7d] text-white font-semibold px-4 py-2 rounded-xl text-xs transition-all shadow-2xs flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                  >
                    <span>{sendingMobileOtp ? 'Sending OTP...' : 'Send OTP'}</span>
                    <ArrowRight size={13} />
                  </button>
                </div>
              </form>
            )}

            {mobileStep === 'OTP' && (
              <form onSubmit={handleVerifyMobileOtp} noValidate className="space-y-3 bg-slate-50/80 p-4 rounded-xl border border-gray-200/80">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-gray-700 truncate pr-2">
                    SMS OTP sent to <span className="font-bold text-gray-900">+91 {newMobile}</span>
                  </span>
                  {mobileTimer > 0 ? (
                    <span className="text-[#2ab391] font-semibold flex-shrink-0">{formatTimer(mobileTimer)}</span>
                  ) : (
                    <span className="text-red-500 font-semibold flex-shrink-0">Expired</span>
                  )}
                </div>

                {/* 6-Digit Mobile OTP Box Grid */}
                <div className="flex items-center justify-center gap-1 sm:gap-2 py-1">
                  {mobileOtp.map((digit, idx) => (
                    <input
                      key={idx}
                      id={`mobile-otp-${idx}`}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => {
                        handleMobileOtpChange(idx, e.target.value);
                        setMobileError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !digit && idx > 0) {
                          const prevInput = document.getElementById(`mobile-otp-${idx - 1}`);
                          if (prevInput) prevInput.focus();
                        }
                      }}
                      className={`w-9 sm:w-10 h-10 sm:h-11 border rounded-xl text-center font-bold text-base focus:outline-none focus:ring-2 focus:ring-[#2ab391] bg-white shadow-2xs transition-all flex-shrink-0 ${
                        mobileError ? 'border-red-500' : 'border-gray-300 focus:border-[#2ab391]'
                      }`}
                    />
                  ))}
                </div>

                {mobileError && (
                  <p className="text-xs text-red-500 font-medium text-center mt-1">{mobileError}</p>
                )}

                <div className="flex items-center justify-between gap-2 pt-0.5">
                  <button
                    type="button"
                    onClick={handleRequestMobileOtp}
                    disabled={sendingMobileOtp || mobileTimer > 150}
                    className="text-[11px] font-semibold text-[#2ab391] hover:underline disabled:opacity-40 flex items-center gap-1 cursor-pointer"
                  >
                    <RefreshCw size={11} /> Resend OTP
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setMobileStep('INPUT');
                        setMobileError('');
                      }}
                      className="text-gray-500 hover:bg-gray-200/60 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={verifyingMobileOtp || mobileOtp.join('').length < 6}
                      className="bg-[#2ab391] hover:bg-[#239b7d] text-white font-semibold px-3.5 py-1.5 rounded-xl text-xs transition-all shadow-xs cursor-pointer disabled:opacity-50 flex items-center gap-1"
                    >
                      <Check size={14} />
                      <span>{verifyingMobileOtp ? 'Verifying...' : 'Verify'}</span>
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default UserProfile;
